import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import ChevronDown from 'components/icons/ChevronDown';
import DatePickerIcon from 'components/icons/DatePicker';
import classNames from 'classnames';

const DEFAULT_MIN_YEAR = 1900;

export default function DatePicker({
    value,
    onChange,
    placeholder = '---- / -- / --',
    disabled = false,
    minDate = null,
    isReadOnly = false,
    allowUnselect = false, 
    overlay = false,
    minYear = null,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
    const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
    const [tempSelectedDate, setTempSelectedDate] = useState(null); // Temporary selected date
    const [isUnselecting, setIsUnselecting] = useState(false); // Track if user is unselecting
    const dropdownRef = useRef(null);
    const calendarRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const selectedDate = value ? new Date(value) : null;

    useEffect(() => {
        if (overlay) {
            return undefined;
        }

        const handleClickOutside = event => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setIsMonthDropdownOpen(false);
                setIsYearDropdownOpen(false);
                setTempSelectedDate(null);
                setIsUnselecting(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [overlay]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        const today = new Date();
        const floorDate = minDate ? new Date(minDate) : null;
        let month = today;

        if (value) {
            month = new Date(value);
        } else if (floorDate && !isNaN(floorDate.getTime()) && floorDate > today) {
            month = floorDate;
        }

        setCurrentDate(month);
        setTempSelectedDate(null);
        setIsUnselecting(false);
        setIsMonthDropdownOpen(false);
        setIsYearDropdownOpen(false);
    }, [isOpen]);

    // In overlay mode the calendar hangs off <body>, so it has to be placed by hand:
    // under the field like the anchored dropdown, and pushed up only as far as the
    // window edge demands — nothing can scroll it back into view out there.
    useLayoutEffect(() => {
        if (!overlay || !isOpen) return undefined;

        const reposition = () => {
            const field = dropdownRef.current;
            const calendar = calendarRef.current;

            if (!field || !calendar) return;

            const rect = field.getBoundingClientRect();
            const gap = 8;

            setCoords({
                top: Math.max(
                    gap,
                    Math.min(rect.bottom + gap, window.innerHeight - calendar.offsetHeight - gap)
                ),
                left: Math.max(
                    gap,
                    Math.min(rect.left, window.innerWidth - calendar.offsetWidth - gap)
                ),
            });
        };

        reposition();
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);

        return () => {
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
        // currentDate is in here because the calendar's height follows the month's row
        // count, and the top clamp above is computed from that height.
    }, [overlay, isOpen, currentDate]);

    // Format date for display
    const formatDate = date => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}/${month}/${day}`;
    };

    // Get calendar data
    const getCalendarData = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // First day of the month
        const firstDay = new Date(year, month, 1);
        // Last day of the month
        const lastDay = new Date(year, month + 1, 0);
        // First day of the week for the first day of month (Monday = 1, Sunday = 0)
        const startDate = new Date(firstDay);
        // Adjust for Monday-first week (getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday)
        const dayOfWeek = firstDay.getDay();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday=0 to 6, others -1
        startDate.setDate(startDate.getDate() - mondayOffset);

        const days = [];
        const current = new Date(startDate);

        // A 31-day month starting on a Saturday or Sunday needs all 6 rows — pinned at 35
        // cells the overflow became unselectable, losing 2026/03/30, 2026/03/31 and
        // 2026/08/31. The 5-row floor keeps the calendar from changing by more than one
        // row between months (only a 28-day month starting Monday fits in 4).
        const weeks = Math.max(5, Math.ceil((mondayOffset + lastDay.getDate()) / 7));

        for (let i = 0; i < weeks * 7; i++) {
            days.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        return {
            year,
            month,
            days,
            firstDay,
            lastDay,
        };
    };

    const { year, month, days } = getCalendarData();

    // Month names in Japanese
    const monthNames = [
        '1月',
        '2月',
        '3月',
        '4月',
        '5月',
        '6月',
        '7月',
        '8月',
        '9月',
        '10月',
        '11月',
        '12月',
    ];

    // Day names in Japanese
    const dayNames = ['月', '火', '水', '木', '金', '土', '日'];

    // Handle date selection (temporary, not saved yet)
    const handleDateSelect = date => {
        if (isDateDisabled(date)) return;

        const dateStr = date.toDateString();

        // Check if this date is currently selected
        // Priority: tempSelectedDate (current temporary selection) > selectedDate (saved selection)
        // But if isUnselecting is true, the date is not considered selected anymore
        let isCurrentlySelected = false;

        if (tempSelectedDate) {
            // If tempSelectedDate exists, use it as the current selection
            isCurrentlySelected = tempSelectedDate.toDateString() === dateStr;
        } else if (!isUnselecting && selectedDate) {
            // If no tempSelectedDate and not unselecting, use selectedDate
            isCurrentlySelected = selectedDate.toDateString() === dateStr;
        }

        // If allowUnselect is true and the clicked date is already selected, unselect it
        if (allowUnselect && isCurrentlySelected) {
            // Unselect: set tempSelectedDate to null and mark as unselecting
            setTempSelectedDate(null);
            setIsUnselecting(true);
            return;
        }

        // Select new date (or re-select if was unselected): clear unselecting flag and set new temp date
        setIsUnselecting(false);
        setTempSelectedDate(date);
    };

    const handleSaveClick = () => {
        // If user is unselecting (isUnselecting is true), unselect the date
        if (isUnselecting && allowUnselect && selectedDate) {
            onChange({ target: { value: null } });
            // Close all dropdowns
            setIsOpen(false);
            setIsMonthDropdownOpen(false);
            setIsYearDropdownOpen(false);
            setTempSelectedDate(null);
            setIsUnselecting(false);
            return;
        }

        // Save the temporary selected date to parent component
        if (tempSelectedDate) {
            const year = tempSelectedDate.getFullYear();
            const month = String(tempSelectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(tempSelectedDate.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;
            onChange({ target: { value: formattedDate } });
        }

        // Close all dropdowns
        setIsOpen(false);
        setIsMonthDropdownOpen(false);
        setIsYearDropdownOpen(false);
        setTempSelectedDate(null); // Clear temporary selection
        setIsUnselecting(false); // Clear unselecting flag
    };

    const handleCancelClick = () => {
        // Discard temporary selection and close dropdowns
        setIsOpen(false);
        setIsMonthDropdownOpen(false);
        setIsYearDropdownOpen(false);
        setTempSelectedDate(null); // Clear temporary selection
        setIsUnselecting(false); // Clear unselecting flag
    };

    // Handle month dropdown toggle
    const handleMonthDropdownToggle = () => {
        setIsMonthDropdownOpen(!isMonthDropdownOpen);
        setIsYearDropdownOpen(false);
    };

    // Handle year dropdown toggle
    const handleYearDropdownToggle = () => {
        setIsYearDropdownOpen(!isYearDropdownOpen);
        setIsMonthDropdownOpen(false);
    };

    // Handle month selection
    const handleMonthSelect = monthIndex => {
        setCurrentDate(prev => new Date(prev.getFullYear(), monthIndex, 1));
        setIsMonthDropdownOpen(false);
    };

    // Handle year selection
    const handleYearSelect = year => {
        setCurrentDate(prev => new Date(year, prev.getMonth(), 1));
        setIsYearDropdownOpen(false);
    };

    // Generate year options (minYear - 2050)
    const getYearOptions = () => {
        const END_YEAR = 2050;
        const minDateYear = minDate ? new Date(minDate).getFullYear() : NaN;
        let floor = DEFAULT_MIN_YEAR;

        if (minYear !== null) {
            floor = minYear;
        } else if (!isNaN(minDateYear)) {
            floor = minDateYear;
        }

        const valueYear = selectedDate ? selectedDate.getFullYear() : NaN;
        const startYear = isNaN(valueYear) ? floor : Math.min(floor, valueYear);

        return Array.from({ length: END_YEAR - startYear + 1 }, (_, i) => startYear + i);
    };

    // Check if date is selected (either saved or temporarily selected)
    const isDateSelected = date => {
        const dateStr = date.toDateString();

        // If tempSelectedDate exists, use it (user has selected a new date)
        // This takes priority over selectedDate
        if (tempSelectedDate) {
            return dateStr === tempSelectedDate.toDateString();
        }

        // If user is unselecting and this date matches the selected date, don't show as selected
        if (isUnselecting && selectedDate && dateStr === selectedDate.toDateString()) {
            return false;
        }

        // Otherwise, use selectedDate if it exists
        if (selectedDate) {
            return dateStr === selectedDate.toDateString();
        }

        return false;
    };

    // Check if date is in current month
    const isCurrentMonth = date => {
        return date.getMonth() === month;
    };

    // Check if date is disabled (before minDate)
    const isDateDisabled = date => {
        if (minYear !== null && date.getFullYear() < minYear) return true;

        if (!minDate) return false;

        const minDateObj = new Date(minDate);
        minDateObj.setHours(0, 0, 0, 0);

        const dateToCheck = new Date(date);
        dateToCheck.setHours(0, 0, 0, 0);

        return dateToCheck < minDateObj;
    };

    // Built once and mounted either on the backdrop or under the field.
    const calendar = (
        <div
            className="dp-dropdown"
            ref={calendarRef}
            style={overlay ? { top: coords.top, left: coords.left } : undefined}
            onMouseDown={event => event.stopPropagation()}
        >
            <div className="dp-header">
                <div className="dp-selectors">
                    <div className="dp-month-selector">
                        <div className="dp-dropdown-container" style={{ position: 'relative' }}>
                            <button
                                type="button"
                                className={`dp-dropdown-button ${
                                    isMonthDropdownOpen ? 'dp-dropdown-button__focus' : ''
                                }`}
                                onClick={handleMonthDropdownToggle}
                            >
                                {monthNames[month]}{' '}
                                <ChevronDown
                                    className="dp-icon"
                                    style={{
                                        fill: 'none',
                                        rotate: isMonthDropdownOpen ? '180deg' : '0deg',
                                        transition: 'transform 0.3s ease-in-out',
                                        width: '18px',
                                        height: '18px',
                                    }}
                                />
                            </button>
                            {isMonthDropdownOpen && (
                                <div className="dp-dropdown-menu dp-dropdown-menu--month">
                                    {monthNames.map((monthName, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`dp-dropdown-item ${
                                                index === month ? 'dp-selected' : ''
                                            }`}
                                            onClick={() => handleMonthSelect(index)}
                                        >
                                            {monthName}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="dp-year-selector">
                        <div className="dp-dropdown-container" style={{ position: 'relative' }}>
                            <button
                                type="button"
                                className={`dp-dropdown-button ${
                                    isYearDropdownOpen ? 'dp-dropdown-button__focus' : ''
                                }`}
                                onClick={handleYearDropdownToggle}
                            >
                                {year}年{' '}
                                <ChevronDown
                                    className="icon"
                                    style={{
                                        fill: 'none',
                                        rotate: isYearDropdownOpen ? '180deg' : '0deg',
                                        transition: 'transform 0.3s ease-in-out',
                                        width: '18px',
                                        height: '18px',
                                    }}
                                />
                            </button>
                            {isYearDropdownOpen && (
                                <div className="dp-dropdown-menu dp-dropdown-menu--year">
                                    {getYearOptions().map(yearOption => (
                                        <button
                                            key={yearOption}
                                            type="button"
                                            className={`dp-dropdown-item ${
                                                yearOption === year ? 'dp-selected' : ''
                                            }`}
                                            onClick={() => handleYearSelect(yearOption)}
                                        >
                                            {yearOption}年
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="dp-calendar">
                <div className="dp-weekdays">
                    {dayNames.map(day => (
                        <div key={day} className="dp-weekday">
                            {day}
                        </div>
                    ))}
                </div>
                <div className="dp-days">
                    {days.map((date, index) => {
                        const disabled = isDateDisabled(date);
                        return (
                            <button
                                key={index}
                                type="button"
                                className={`dp-day ${
                                    !isCurrentMonth(date) ? 'dp-other-month' : ''
                                } ${isDateSelected(date) ? 'dp-selected' : ''} ${
                                    disabled ? 'dp-disabled' : ''
                                }`}
                                onClick={() => handleDateSelect(date)}
                                disabled={disabled}
                                style={{
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.4 : 1,
                                }}
                            >
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="dp-footer">
                <button type="button" className="dp-btn-cancel" onClick={handleCancelClick}>
                    キャンセル
                </button>
                <button type="button" className="dp-btn-save" onClick={handleSaveClick}>
                    保存する
                </button>
            </div>
        </div>
    );

    return (
        <div className="dp-datepicker" ref={dropdownRef}>
            <div className="dp-input-container">
                <button
                    type="button"
                    className={classNames('btn btn-outline-gray dp-button', {
                        'dp-button__selected': isOpen || selectedDate || tempSelectedDate,
                    })}
                    onClick={() => {
                        setIsOpen(!isOpen);
                    }}
                    disabled={disabled && !isReadOnly}
                    style={{
                        backgroundColor: 'transparent',
                    }}
                >
                    {selectedDate ? formatDate(selectedDate) : placeholder}
                    <div className="dp-icon-wrapper">
                        <DatePickerIcon className="icon dp-icon" />
                    </div>
                </button>
            </div>

            {isOpen &&
                (overlay
                    ? ReactDOM.createPortal(
                          // Anchored to the field the calendar ran off screen inside the
                          // short CSV dialog, taking its 保存する button with it. Portalling
                          // it out fixes that; the backdrop is what closes it.
                          <div className="dp-overlay" onMouseDown={handleCancelClick}>
                              {calendar}
                          </div>,
                          document.body
                      )
                    : calendar)}
        </div>
    );
}
